/**
 * Admin Route Component
 *
 * Protects admin routes by checking if the user has ADMIN role.
 * Redirects non-admin users to /home.
 *
 * @fileoverview Route protection component for admin-only routes
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import {
  Center,
  Spinner,
  VStack,
  Text,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Button,
} from '@chakra-ui/react';
import { useAuth } from '../../hooks/useAuth';
import { useUserProfile } from '../../hooks/useUserProfile';

interface AdminRouteProps {
  children: React.ReactNode;
}

/**
 * Admin Route Component
 *
 * Wraps admin routes and checks if the user has ADMIN role.
 * - Shows loading spinner while checking auth/profile
 * - Redirects to / if not authenticated
 * - Redirects to /home if authenticated but not admin
 * - Renders children if user is admin
 *
 * @param props - Component props
 * @returns Admin route content or redirect
 */
export const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { user, loading: authLoading, mfaRequired, checkingMfa } = useAuth();
  const { profile, loading: profileLoading, error, isAdmin, refetch } = useUserProfile();
  const location = useLocation();
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const brandColor = useColorModeValue('brand.secondary', 'brand.secondaryLight');

  // Show spinner only when we don't yet have the data needed to make
  // an access decision. Once profile is loaded, skip the overlay so that
  // background token refreshes never blank the screen.
  if (authLoading || checkingMfa || (profileLoading && !profile)) {
    return (
      <Center minH="100vh" bg={bgColor}>
        <VStack spacing={4}>
          <Spinner size="xl" color={brandColor} thickness="4px" />
          <Text color="gray.500">Checking access...</Text>
        </VStack>
      </Center>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Unlike ProtectedRoute (which every other authenticated route goes
  // through), this guard never checked mfaRequired. useUserProfile()'s own
  // fetch below is behind MfaGuard on the backend, so an unverified admin
  // landing here directly (a bookmark, a refresh, the post-login redirect
  // itself) got that fetch's 403 rendered as a dead-end "Error Loading
  // Profile" screen instead of being routed to complete MFA — the "Try
  // Again" button just repeated the same 403 forever.
  if (mfaRequired) {
    return <Navigate to="/mfa-verify" replace />;
  }

  // Show error state if profile fetch failed
  if (error) {
    return (
      <Center minH="100vh" bg={bgColor}>
        <VStack spacing={4} p={8}>
          <Alert status="error" borderRadius="md" flexDirection="column" textAlign="center" py={8}>
            <AlertIcon boxSize={10} mr={0} mb={4} />
            <AlertTitle fontSize="lg" mb={2}>Error Loading Profile</AlertTitle>
            <AlertDescription mb={4}>{error}</AlertDescription>
            <Button colorScheme="blue" onClick={() => refetch()}>
              Try Again
            </Button>
          </Alert>
        </VStack>
      </Center>
    );
  }

  // Only redirect once we have definitive profile data
  if (!profileLoading && !isAdmin) {
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};

export default AdminRoute;
